import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Package, Truck, CheckCircle, Clock, Search, MapPin, Shield, Headphones, CreditCard, Loader2 } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { OrderConfirmation } from "@/components/buyer/OrderConfirmation";
import { SupportCenter } from "@/components/shared/SupportCenter";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { PageHeader } from "@/components/shared/PageHeader";
import { PullToRefresh } from "@/components/shared/PullToRefresh";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";

export default function BuyerOrders() {
  const [trackingNumber, setTrackingNumber] = useState("");
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<any[]>([]);

  useEffect(() => {
    if (user?.id) {
      fetchOrders();
    }
  }, [user]);

  // Refresh orders when page becomes visible (for post-payment redirect)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user?.id) {
        fetchOrders();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user]);

  const fetchOrders = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      const { data: ordersData } = await supabase
        .from('orders')
        .select('*')
        .eq('buyer_id', user.id)
        .order('created_at', { ascending: false });

      if (ordersData) {
        const formattedOrders = await Promise.all(
          ordersData.map(async (order) => {
            const { data: seller } = await supabase
              .from('profiles')
              .select('business_name')
              .eq('id', order.seller_id)
              .single();
            
            const items = order.items as any[];
            const isSelfDelivered = order.tracking_number?.startsWith('SELF-');
            const isDelivered = order.status === 'delivered' || order.delivery_status === 'delivered';
            
            // Determine escrow status based on payment and delivery
            let escrowStatus: string | undefined;
            if (order.payment_status === 'completed') {
              if (isDelivered) {
                escrowStatus = 'released';
              } else {
                escrowStatus = 'held';
              }
            }
            
            return {
              id: order.id, // Keep full UUID for database operations
              displayId: order.id.slice(0, 8).toUpperCase(), // For display purposes
              seller: seller?.business_name || 'Seller',
              items: items?.map(item => item.title) || [],
              totalAmount: `₦${Number(order.total_amount).toLocaleString()}`,
              totalAmountRaw: Number(order.total_amount),
              status: order.status,
              paymentStatus: order.payment_status,
              escrowStatus,
              deliveryStatus: order.delivery_status || order.status,
              trackingNumber: order.tracking_number || `TRK${order.id.slice(0, 10).toUpperCase()}`,
              isSelfDelivered,
              createdAt: new Date(order.created_at).toLocaleDateString()
            };
          })
        );
        setOrders(formattedOrders);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  const { isRefreshing, pullDistance, triggerRefresh } = usePullToRefresh({
    onRefresh: fetchOrders,
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'processing': return <Clock className="h-4 w-4" />;
      case 'shipped': return <Truck className="h-4 w-4" />;
      case 'delivered': return <CheckCircle className="h-4 w-4" />;
      default: return <Package className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'processing': return 'bg-yellow-500';
      case 'shipped': return 'bg-blue-500';
      case 'delivered': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <PullToRefresh pullDistance={pullDistance} isRefreshing={isRefreshing} />
      <div className="container mx-auto px-4 py-8">
        <PageHeader
          title="My Orders"
          description="Track and manage your purchases"
          backTo="/"
          onRefresh={triggerRefresh}
          isRefreshing={isRefreshing}
        />

        {/* Quick Track */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Quick Track</CardTitle>
            <CardDescription>Enter your tracking number to get instant updates</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4">
              <Input
                placeholder="Enter tracking number (e.g., TRK789456123)"
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                className="flex-1"
              />
              <Button variant="gradient" className="w-full sm:w-auto">
                <Search className="h-4 w-4 mr-2" />
                Track Order
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Orders Tabs */}
        <Tabs defaultValue="all" className="space-y-6">
          <TabsList className="w-full overflow-x-auto flex justify-start no-scrollbar">
            <TabsTrigger value="all" className="text-xs sm:text-sm whitespace-nowrap flex-shrink-0">All Orders</TabsTrigger>
            <TabsTrigger value="active" className="text-xs sm:text-sm whitespace-nowrap flex-shrink-0">Active</TabsTrigger>
            <TabsTrigger value="completed" className="text-xs sm:text-sm whitespace-nowrap flex-shrink-0">Completed</TabsTrigger>
            <TabsTrigger value="support" className="text-xs sm:text-sm whitespace-nowrap flex-shrink-0">
              <Headphones className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              Support
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all">
            <div className="space-y-6">
              {loading ? (
                <Card>
                  <CardContent className="pt-6 flex items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin mr-2" />
                    <span>Loading orders...</span>
                  </CardContent>
                </Card>
              ) : orders.length === 0 ? (
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-muted-foreground text-center">No orders found</p>
                  </CardContent>
                </Card>
              ) : (
                orders.map((order) => (
                <Card key={order.id}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">Order {order.displayId}</CardTitle>
                        <CardDescription>{order.seller}</CardDescription>
                      </div>
                      <div className="flex flex-col gap-1 items-end">
                        <Badge className={`${getStatusColor(order.status)} text-white`}>
                          {getStatusIcon(order.status)}
                          <span className="ml-1 capitalize">{order.status}</span>
                        </Badge>
                        {order.isSelfDelivered && (
                          <Badge variant="outline" className="text-xs">Self-Delivery</Badge>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* Order Items */}
                      <div>
                        <p className="text-sm font-medium mb-1">Items:</p>
                        <p className="text-sm text-muted-foreground">{order.items.join(", ")}</p>
                      </div>

                      {/* Order Amount */}
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Total Amount:</span>
                        <span className="font-bold text-lg">{order.totalAmount}</span>
                      </div>

                      {/* Tracking Info */}
                      {order.trackingNumber && (
                        <div className="border-t pt-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium">Tracking Number:</span>
                            <code className="text-xs bg-muted px-2 py-1 rounded">{order.trackingNumber}</code>
                          </div>
                          
                          {order.currentLocation && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                              <MapPin className="h-3 w-3" />
                              Current Location: {order.currentLocation}
                            </div>
                          )}

                          {order.estimatedDelivery && (
                            <p className="text-sm text-muted-foreground mb-3">
                              Estimated Delivery: <span className="font-medium">{order.estimatedDelivery}</span>
                            </p>
                          )}

                          {order.deliveredDate && (
                            <p className="text-sm text-green-600">
                              Delivered on: <span className="font-medium">{order.deliveredDate}</span>
                            </p>
                          )}

                          {/* Tracking History */}
                          {order.trackingHistory && (
                            <div className="mt-4">
                              <p className="text-sm font-medium mb-3">Tracking History:</p>
                              <div className="space-y-3">
                                {order.trackingHistory.map((event, index) => (
                                  <div key={index} className="flex gap-3">
                                    <div className="flex flex-col items-center">
                                      <div className={`w-3 h-3 rounded-full ${index === 0 ? 'bg-primary' : 'bg-muted'}`} />
                                      {index < order.trackingHistory.length - 1 && (
                                        <div className="w-0.5 h-12 bg-muted mt-1" />
                                      )}
                                    </div>
                                    <div className="flex-1 -mt-0.5">
                                      <p className="text-sm font-medium">{event.status}</p>
                                      <p className="text-xs text-muted-foreground">
                                        {event.date} at {event.time} • {event.location}
                                      </p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Escrow Protection & Delivery Confirmation */}
                      {order.escrowStatus && order.paymentStatus === 'completed' && (
                        <div className="border-t pt-4">
                          <OrderConfirmation
                            orderId={order.id}
                            orderStatus={order.status}
                            deliveryStatus={order.deliveryStatus || order.status}
                            escrowStatus={order.escrowStatus}
                            totalAmount={order.totalAmountRaw}
                            isSelfDelivered={order.isSelfDelivered}
                            onConfirm={() => {
                              fetchOrders();
                            }}
                          />
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex gap-2 pt-2">
                        {order.status === 'delivered' && !order.escrowStatus && (
                          <Button variant="outline" className="flex-1">Rate Product</Button>
                        )}
                        {order.status !== 'delivered' && (
                          <Button variant="outline" className="flex-1">Contact Seller</Button>
                        )}
                        <Button variant="gradient" className="flex-1">View Details</Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="active">
            <Card>
              <CardContent className="pt-6">
                <p className="text-muted-foreground text-center">Your active orders will appear here</p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="completed">
            <Card>
              <CardContent className="pt-6">
                <p className="text-muted-foreground text-center">Your completed orders will appear here</p>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="support">
            <SupportCenter userType="buyer" />
          </TabsContent>
        </Tabs>

        {/* Quick Actions */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Link to="/buyer/bank-accounts">
                <Button variant="outline" size="sm">
                  <CreditCard className="mr-2 h-4 w-4" />
                  Manage Bank Accounts
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}